import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TvIcon from '@mui/icons-material/Tv';
import WifiIcon from '@mui/icons-material/Wifi';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import SurroundSoundIcon from '@mui/icons-material/SurroundSound';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const Services = () => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const services = [
    {
      title: 'TV Mounting & Installation',
      icon: <TvIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      description: 'Professional TV mounting service with clean cable management',
      features: [
        'Wall mount installation',
        'Cable concealment',
        'Level adjustment',
        'Sturdy mounting guaranteed',
      ],
      price: 'Starting at $99',
      details: [
        'Expert mounting for all TV sizes',
        'Proper height and viewing angle setup',
        'Hidden cable management system',
        'Wall type assessment and appropriate mounting',
      ],
    },
    {
      title: 'Smart TV Setup & Activate',
      icon: <WifiIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      description: 'Complete smart TV configuration and streaming setup',
      features: [
        'WiFi connection setup',
        'Streaming apps installation',
        'Account configuration',
        'Smart home integration',
      ],
      price: 'Starting at $79',
      details: [
        'Network optimization for streaming',
        'Multiple streaming service setup',
        'Smart home device integration',
        'Voice control configuration',
      ],
    },
    {
      title: 'Universal Remote Setup',
      icon: <SettingsRemoteIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      description: 'Configure one remote to control all your devices',
      features: [
        'Multiple device programming',
        'Custom button mapping',
        'Voice control setup',
        'User training',
      ],
      price: 'Starting at $49',
      details: [
        'Programming for all major brands',
        'Custom macro configuration',
        'Backup of remote settings',
        'Comprehensive user guide',
      ],
    },
    {
      title: 'Home Theater Installation',
      icon: <SurroundSoundIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      description: 'Complete home theater setup and calibration',
      features: [
        'Speaker placement & mounting',
        'Audio calibration',
        'Receiver setup',
        'Cable management',
      ],
      price: 'Starting at $199',
      details: [
        'Acoustic room analysis',
        'Premium cable routing',
        'Sound field optimization',
        'Complete system testing',
      ],
    },
  ];

  const processSteps = [
    'Initial Consultation',
    'Site Assessment',
    'Installation Planning',
    'Professional Setup',
    'Testing & Calibration',
    'Customer Training',
  ];

  const faqs = [
    {
      question: 'How long does a typical TV installation take?',
      answer: 'A standard TV mounting typically takes 1-2 hours, depending on the complexity of the installation and whether cable concealment is required.',
    },
    {
      question: 'Do you provide the mounting hardware?',
      answer: "Yes, we provide all necessary mounting hardware and ensure it's appropriate for your TV size and wall type.",
    },
    {
      question: 'Can you mount a TV on any type of wall?',
      answer: 'We can mount TVs on most wall types including drywall, concrete, and brick. Our technicians will assess the wall structure to ensure safe mounting.',
    },
    {
      question: 'Do you offer warranty on installations?',
      answer: 'Yes, all our installations come with a 1-year warranty on workmanship, plus we honor all manufacturer warranties.',
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardHover = {
    scale: 1.02,
    transition: { duration: 0.2 }
  };

  return (
    <PageTransition>
      <Box>
        {/* Services Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
          <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
            <motion.div {...fadeInUp}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  textAlign: 'center',
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: 'white'
                }}
              >
                Our Services
              </Typography>
            </motion.div>
            <motion.div {...fadeInUp}>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4,
                  textAlign: 'center',
                  maxWidth: '900px',
                  margin: '0 auto',
                  opacity: 0.9,
                  fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                  lineHeight: 1.4,
                }}
              >
                Professional TV Setup Solutions for Your Home
              </Typography>
            </motion.div>
          </Container>
        </Box>

        {/* Services Grid */}
        <Container sx={{ py: 8 }}>
          <motion.div variants={staggerChildren} initial="initial" animate="animate">
            <Grid container spacing={4}>
              {services.map((service, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div variants={fadeInUp}>
                    <motion.div whileHover={cardHover}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {service.icon}
                            <Typography variant="h5" component="h2" sx={{ ml: 2 }}>
                              {service.title}
                            </Typography>
                          </Box>
                          <Typography variant="body1" color="text.secondary" paragraph>
                            {service.description}
                          </Typography>
                          <List>
                            {service.features.map((feature, idx) => (
                              <ListItem key={idx} dense>
                                <ListItemIcon>
                                  <CheckCircleIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={feature} />
                              </ListItem>
                            ))}
                          </List>
                          <Typography
                            variant="h6"
                            color="primary"
                            sx={{ mt: 2, fontWeight: 'bold' }}
                          >
                            {service.price}
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" gutterBottom>
                            Additional Features:
                          </Typography>
                          <List>
                            {service.details.map((detail, idx) => (
                              <ListItem key={idx} dense>
                                <ListItemIcon>
                                  <CheckCircleIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={detail} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                        <CardActions sx={{ mt: 'auto', p: 2 }}>
                          <Button
                            component={RouterLink}
                            to="/contact"
                            variant="contained"
                            fullWidth
                          >
                            Book Now
                          </Button>
                        </CardActions>
                      </Card>
                    </motion.div>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>

        {/* Installation Process */}
        <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
          <Container>
            <motion.div {...fadeInUp}>
              <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
                Our Installation Process
              </Typography>
              <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
                We follow a systematic approach to ensure quality installation
              </Typography>
            </motion.div>
            <motion.div variants={staggerChildren} initial="initial" animate="animate">
              <Stepper activeStep={-1} alternativeLabel>
                {processSteps.map((label, index) => (
                  <motion.div key={index} variants={fadeInUp}>
                    <Step>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  </motion.div>
                ))}
              </Stepper>
            </motion.div>
          </Container>
        </Box>

        {/* FAQs */}
        <Container sx={{ py: 8 }}>
          <motion.div {...fadeInUp}>
            <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
              Frequently Asked Questions
            </Typography>
          </motion.div>
          <motion.div variants={staggerChildren} initial="initial" animate="animate">
            <Box sx={{ mt: 4 }}>
              {faqs.map((faq, index) => (
                <motion.div key={index} variants={fadeInUp}>
                  <Accordion
                    expanded={expanded === `panel${index}`}
                    onChange={handleChange(`panel${index}`)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}bh-content`}
                      id={`panel${index}bh-header`}
                    >
                      <Typography variant="h6">{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>

        {/* Call to Action */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
          <Container>
            <motion.div {...fadeInUp}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h4" component="h2" gutterBottom>
                    Need a custom solution?
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Contact us for a personalized quote tailored to your specific needs
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Get Custom Quote
                  </Button>
                </Grid>
              </Grid>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default Services; 